import { makeStyles } from '@material-ui/core';
import { format, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React from 'react';
import Typography from '~components/typography';
import { BOOKING_CATEGORY } from '~graphql-types';
import { BasicInstitution, BasicLaptop } from '~svg-components';
import { FORMAT_DAY_DATE_MONTH_YEAR } from '~utils/date-format';
import BookingStatusChip from '../booking/booking-details/booking-status-chip';

const useStyles = makeStyles(
  (theme) => ({
    root: {
      minWidth: '360px',
      padding: '12px 12px 8px 16px',
      minHeight: '72px',
      marginBottom: '12px',
      backgroundColor: theme.colors.white,
      borderRadius: theme.borderRadius.small,
      border: `1px solid ${theme.colors.greyE8}`,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.colors.greyFa,
      },
    },

    bookingStatusLabel: {
      ...theme.typography.body2,
      padding: '0 8px',
    },

    lineOne: {
      display: 'flex',
      alignItems: 'flex-start',
    },

    lineOneLeft: {
      flex: 1,
      transform: 'translateY(-2px)',
    },

    lineOneRight: {},

    username: {
      display: 'inline',
      marginRight: '8px',
    },

    bookingCategoryIcon: {
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: 20,
      height: 20,
      backgroundColor: theme.colors.greyE8,
      borderRadius: '50%',
      color: theme.colors.grey66,
      transform: 'translateY(2px)',
      marginRight: '8px',
      '& > svg': {
        alignSelf: 'center',
        width: '14px',
        height: '14px',
      },
    },

    selectionBed: {
      display: 'inline-block',
      backgroundColor: theme.colors.greyE8,
      borderRadius: theme.borderRadius.huge,
      padding: '0 8px',
      marginRight: '8px',
      verticalAlign: 'text-bottom',
      transform: 'translateY(2px)',
    },

    bookingStatusConfirm: {
      backgroundColor: theme.colors.greenBg,
      borderRadius: theme.borderRadius.huge,
      padding: '0 8px',
      marginRight: '8px',
      justifyContent: 'flex-end',
    },

    bookingStatusWaitingConfirm: {
      backgroundColor: theme.colors.orangeBg,
      borderRadius: theme.borderRadius.huge,
      padding: '0 8px',
      marginRight: '8px',
      justifyContent: 'flex-end',
    },

    lineTwo: {
      ...theme.flex.crossCenter,
    },

    tenancyOption: {},

    divider: {
      margin: '0 8px',
    },
  }),
  { name: 'BookingCard' }
);

function BookingCardRecentlyViewed({ booking }) {
  const classes = useStyles();
  const {
    student: { fullName },
    roomAllocation: { bedName },
    status,
    tenancyOption: { name, endDate, startDate },
    category,
    id,
  } = booking;

  const renderStudentName = () => (
    <Typography variant="body1" themeColor="dark" className={classes.username}>
      {fullName}
    </Typography>
  );

  const renderBookingCategoryTag = () => (
    <div className={classes.bookingCategoryIcon}>
      {category === BOOKING_CATEGORY.NOMINATION ? <BasicInstitution /> : <BasicLaptop />}
    </div>
  );

  const renderSelectionBed = () => (
    <Typography variant="body2" themeColor="grey66" className={classes.selectionBed}>
      {bedName}
    </Typography>
  );

  const renderTenancyOptionName = () => (
    <Typography variant="body2" themeColor="grey66" className={classes.tenancyOption}>
      {name || '-'}
    </Typography>
  );

  const renderDivider = () => (
    <Typography variant="body1" themeColor="greyE8" className={classes.divider}>
      |
    </Typography>
  );

  const renderCheckInCheckOut = () => (
    <Typography variant="body2" themeColor="grey66" className={classes.tenancyOption}>
      {`${format(parseISO(startDate), FORMAT_DAY_DATE_MONTH_YEAR)} - ${format(
        parseISO(endDate),
        FORMAT_DAY_DATE_MONTH_YEAR
      )}`}
    </Typography>
  );

  const openBookingDetailPage = () => {
    const bookingDetailsUrl = queryString.stringifyUrl({
      url: '/booking/booking-details',
      query: { originBookingId: JSON.parse(atob(id))?.id },
    });
    window.open(bookingDetailsUrl);
  };

  return (
    <div
      className={classes.root}
      onClick={() => {
        openBookingDetailPage();
      }}
    >
      <div className={classes.lineOne}>
        <div className={classes.lineOneLeft}>
          {renderStudentName()}
          {renderBookingCategoryTag()}
          {renderSelectionBed()}
        </div>
        <div className={classes.lineOneRight}>
          <BookingStatusChip status={status} classes={{ label: classes.bookingStatusLabel }} />
        </div>
      </div>

      <div className={classes.lineTwo}>
        {renderTenancyOptionName()}
        {name && startDate && endDate && renderDivider()}
        {startDate && endDate && renderCheckInCheckOut()}
      </div>
    </div>
  );
}

BookingCardRecentlyViewed.prototype = {
  booking: PropTypes.object.isRequired,
};

export default BookingCardRecentlyViewed;
